import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core";
import { find, filter, map, flatMap, forEach } from "lodash";
import * as Highcharts from "highcharts";

@Component({
  selector: "line-expanded",
  templateUrl: "./line-expanded.component.html",
  styleUrls: ["./line-expanded.component.scss"],
})
export class LineExpandedComponent implements OnInit {
  options: Object;
  chart;
  toolTipText: string;
  toolTipTitle: string;
  hasChartData: boolean;
  Highcharts = Highcharts;
  @Input()
  chartData;
  @Input()
  categories;
  @Input()
  dataSuffix;
  @Input()
  dataPrefix;
  @Input()
  min;
  @Input()
  max;
  @Input()
  roundTo = 0;
  @Input()
  title;
  @Input()
  tooltipDescriptor;
  @Input()
  filterByText;
  @Input()
  contentText;
  @Input()
  compareByCategory;
  @Input()
  noComparisonText;

  @Output() lineMousedOver = new EventEmitter();
  @Output() lineMousedOut = new EventEmitter();

  constructor() {}

  ngOnInit() {
    this.hasChartData = this.checkForChartData(this.chartData);
    this.createChart();
  }

  updateChartWithNewData(newData) {
    this.hasChartData = this.checkForChartData(newData.series);
    this.chart.update(newData);
  }

  chartCallback = (chart) => {
    this.chart = chart;
    setTimeout(() => {
      if (chart.chartHeight !== chart.renderTo.clientHeight) {
        chart.setSize(undefined, null, true);
      }
    });
  };

  createChart() {
    var self = this;
    this.options = {
      chart: {
        spacingRight: 0,
        animation: false,
      },
      boost: {
        allowForce: true, // The whole chart will be boosted if all of its series are supported by the boost module, and if at least one series crosses its boost threshold.
      },
      xAxis: {
        type: "category",
        categories: this.categories,
        tickmarkPlacement: "on",
        tickPosition: "outside",
        tickColor: "#929292",
        tickWidth: 0.75,
        lineColor: "#929292",
        lineWidth: 0.75,
        startOnTick: true,
        labels: {
          formatter: function () {
            return `<span style="fill: #929292; text-overflow: none"> ${this.value} </span>`;
          },
        },
      },
      yAxis: {
        min: Math.floor(this.min),
        max: Math.ceil(this.max),
        gridLineWidth: 0,
        lineColor: "#929292",
        lineWidth: 0.75,
        labels: {
          formatter: function () {
            return `<span style="fill: #929292; text-overflow: none"> ${
              this.value
            }${self.dataSuffix ? self.dataSuffix : ""}</span>`;
          },
        },
        title: {
          text: this.title,
        },
      },
      title: { text: null },
      series: this.chartData,
      credits: false,
      plotOptions: {
        line: {
          marker: {
            symbol: "circle",
          },
          lineWidth: 1.5,
          color: "#b4b4b4",
          states: {
            hover: {
              enabled: true,
              radiusPlus: 5,
              halo: false,
              marker: {
                fillColor: "#f5a847",
              },
            },
          },
        },
        series: {
          label: {
            enabled: false,
          },
          animation: false,
          dataLabels: {
            enabled: true,
            formatter: function () {
              let len =
                this.series && this.series.points
                  ? this.series.points.length - 1
                  : null;
              let y =
                this.y != null
                  ? this.y.toLocaleString(undefined, {
                      maximumFractionDigits: self.roundTo,
                    })
                  : "";
              return len != null &&
                this.point.index == len &&
                this.series &&
                this.series.chart &&
                this.series.chart.series &&
                this.series.chart.series.length < 8
                ? `<div style="fill: ${this.series.color}; font-weight:200">${
                    self.dataPrefix || ""
                  }${y}${self.dataSuffix || ""}</div><br>
               <div style="fill: ${this.series.color}; font-weight:200">${
                    this.series.name
                  }</div>`
                : `<div style="fill: ${this.series.color}; font-weight:200">${
                    self.dataPrefix || ""
                  }${y}${self.dataSuffix || ""}</div>`;
            },
          },
          events: {
            mouseOver: function () {
              forEach(this.points, (point) => {
                point.color = "#f5a847";
              });
              // handle case when series is technically visible but not drawn in the highcart because it has no data for the selected timeperiod
              if (this.graph) {
                this.graph.attr({ stroke: "#f5a847" });
              }
              this.drawPoints();
              self.lineMousedOver.emit(this.index);
            },
            mouseOut: function () {
              forEach(this.points, (point) => {
                point.color = "#b4b4b4";
              });
              // handle case when series is technically visible but not drawn in the highcart because it has no data for the selected timeperiod
              if (this.graph) {
                this.graph.attr({ stroke: "#b4b4b4" });
              }
              this.drawPoints();
              self.lineMousedOut.emit(this.index);
            },
          },
          stickyTracking: false,
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        shared: false,
        outside: false, //setting outside to false here because it was making the tooltip show up behind the MZ modal
        useHTML: true,
        borderColor: "#aaa",
        backgroundColor: "rgba(255, 255, 255, 1.0)",
        shadow: false,
        headerFormat: "",
        style: {
          fontFamily: "'Lato', Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        },
        formatter: function () {
          if (self.dataSuffix === "") {
            self.toolTipText = `${self.dataPrefix || ""}<strong>${
              this.point.numerator != null
                ? this.point.numerator.toLocaleString(undefined, {
                    maximumFractionDigits: self.roundTo,
                  })
                : ""
            }</strong> ${self.tooltipDescriptor}`;
          } else {
            self.toolTipText = `${self.dataPrefix || ""}<strong>${
              this.point.numerator != null
                ? this.point.numerator.toLocaleString(undefined, {
                    maximumFractionDigits: self.roundTo,
                  })
                : ""
            }</strong> of ${self.dataPrefix || ""}<strong>${
              this.point.denominator != null
                ? this.point.denominator.toLocaleString(undefined, {
                    maximumFractionDigits: self.roundTo,
                  })
                : ""
            }</strong> ${self.tooltipDescriptor}`;
          }
          self.toolTipTitle = `${this.point.name} ${this.series.name}`;
          let titleText =
            `<span class="metric-tooltip title capitalize tile_body">` +
            (self.contentText && this.series.userOptions.content
              ? `<div>
                 Content: 
                 <span class="body_tooltip_text"><strong>${this.series.userOptions.content}</strong></span>
               </div>`
              : ``) +
            (self.compareByCategory &&
            self.compareByCategory != self.noComparisonText
              ? `<div>
                 ${self.compareByCategory}: 
                 <span class="body_tooltip_text"><strong>${this.series.userOptions.compare_by}</strong></span>
               </div>`
              : ``) +
            (self.filterByText
              ? `<div>
                 Filtered by: 
                 <span class="body_tooltip_text"><strong>${self.filterByText}</strong></span>
               </div>`
              : ``) +
            (this.point.name
              ? `<div>
                 Time Period: 
                 <span class="body_tooltip_text"><strong>${this.point.name}</strong></span>
               </div>`
              : ``) +
            `</span>`;
          return (
            `<div class="line-expanded-titles">
                      ${titleText}
                      <span class="metric-tooltip value body_tooltip_text"><strong>` +
            (this.point.alphaValue
              ? this.point.alphaValue
              : `${self.dataPrefix || ""}${
                  this.point.y != null
                    ? this.point.y.toLocaleString(undefined, {
                        minimumFractionDigits: self.roundTo,
                        maximumFractionDigits: self.roundTo,
                      })
                    : ""
                }${self.dataSuffix}`) +
            `</strong></span>
                    </div>
                    <div class="metric-tooltip description body_tooltip_text">${self.toolTipText}</div>`
          );
        },
        positioner: function (boxWidth, boxHeight, point) {
          let tooltipArrowOffset = 20;
          let pointX;
          let pointY;
          if (point.plotY < boxHeight / 2) {
            pointY = point.plotY + tooltipArrowOffset;
          } else {
            pointY = point.plotY - boxHeight;
          }
          if (point.plotX < boxWidth - tooltipArrowOffset) {
            pointX = 0;
          } else {
            pointX = point.plotX - boxWidth + tooltipArrowOffset;
          }
          return { x: pointX, y: pointY };
        },
      },
    };
  }

  checkForChartData(data) {
    // get an array of defined chart data points
    let chartDataPts = filter(flatMap(data, "data"), (value) => {
      return value != undefined;
    });
    // check that the above array is defined and has at least one element
    return chartDataPts != undefined && chartDataPts.length > 0;
  }
}
